/**
 * State mapping utilities for converting between state names, slugs, and area codes
 */

interface StateMapping {
  name: string;
  slug: string;
  code: string; // Area code for BLS data
  abbreviation: string;
}

export const STATE_MAPPINGS: StateMapping[] = [
  { name: "Alabama", slug: "alabama", code: "01", abbreviation: "AL" },
  { name: "Alaska", slug: "alaska", code: "02", abbreviation: "AK" },
  { name: "Arizona", slug: "arizona", code: "04", abbreviation: "AZ" },
  { name: "Arkansas", slug: "arkansas", code: "05", abbreviation: "AR" },
  { name: "California", slug: "california", code: "06", abbreviation: "CA" },
  { name: "Colorado", slug: "colorado", code: "08", abbreviation: "CO" },
  { name: "Connecticut", slug: "connecticut", code: "09", abbreviation: "CT" },
  { name: "Delaware", slug: "delaware", code: "10", abbreviation: "DE" },
  { name: "Florida", slug: "florida", code: "12", abbreviation: "FL" },
  { name: "Georgia", slug: "georgia", code: "13", abbreviation: "GA" },
  { name: "Hawaii", slug: "hawaii", code: "15", abbreviation: "HI" },
  { name: "Idaho", slug: "idaho", code: "16", abbreviation: "ID" },
  { name: "Illinois", slug: "illinois", code: "17", abbreviation: "IL" },
  { name: "Indiana", slug: "indiana", code: "18", abbreviation: "IN" },
  { name: "Iowa", slug: "iowa", code: "19", abbreviation: "IA" },
  { name: "Kansas", slug: "kansas", code: "20", abbreviation: "KS" },
  { name: "Kentucky", slug: "kentucky", code: "21", abbreviation: "KY" },
  { name: "Louisiana", slug: "louisiana", code: "22", abbreviation: "LA" },
  { name: "Maine", slug: "maine", code: "23", abbreviation: "ME" },
  { name: "Maryland", slug: "maryland", code: "24", abbreviation: "MD" },
  { name: "Massachusetts", slug: "massachusetts", code: "25", abbreviation: "MA" },
  { name: "Michigan", slug: "michigan", code: "26", abbreviation: "MI" },
  { name: "Minnesota", slug: "minnesota", code: "27", abbreviation: "MN" },
  { name: "Mississippi", slug: "mississippi", code: "28", abbreviation: "MS" },
  { name: "Missouri", slug: "missouri", code: "29", abbreviation: "MO" },
  { name: "Montana", slug: "montana", code: "30", abbreviation: "MT" },
  { name: "Nebraska", slug: "nebraska", code: "31", abbreviation: "NE" },
  { name: "Nevada", slug: "nevada", code: "32", abbreviation: "NV" },
  { name: "New Hampshire", slug: "new-hampshire", code: "33", abbreviation: "NH" },
  { name: "New Jersey", slug: "new-jersey", code: "34", abbreviation: "NJ" },
  { name: "New Mexico", slug: "new-mexico", code: "35", abbreviation: "NM" },
  { name: "New York", slug: "new-york", code: "36", abbreviation: "NY" },
  { name: "North Carolina", slug: "north-carolina", code: "37", abbreviation: "NC" },
  { name: "North Dakota", slug: "north-dakota", code: "38", abbreviation: "ND" },
  { name: "Ohio", slug: "ohio", code: "39", abbreviation: "OH" },
  { name: "Oklahoma", slug: "oklahoma", code: "40", abbreviation: "OK" },
  { name: "Oregon", slug: "oregon", code: "41", abbreviation: "OR" },
  { name: "Pennsylvania", slug: "pennsylvania", code: "42", abbreviation: "PA" },
  { name: "Rhode Island", slug: "rhode-island", code: "44", abbreviation: "RI" },
  { name: "South Carolina", slug: "south-carolina", code: "45", abbreviation: "SC" },
  { name: "South Dakota", slug: "south-dakota", code: "46", abbreviation: "SD" },
  { name: "Tennessee", slug: "tennessee", code: "47", abbreviation: "TN" },
  { name: "Texas", slug: "texas", code: "48", abbreviation: "TX" },
  { name: "Utah", slug: "utah", code: "49", abbreviation: "UT" },
  { name: "Vermont", slug: "vermont", code: "50", abbreviation: "VT" },
  { name: "Virginia", slug: "virginia", code: "51", abbreviation: "VA" },
  { name: "Washington", slug: "washington", code: "53", abbreviation: "WA" },
  { name: "West Virginia", slug: "west-virginia", code: "54", abbreviation: "WV" },
  { name: "Wisconsin", slug: "wisconsin", code: "55", abbreviation: "WI" },
  { name: "Wyoming", slug: "wyoming", code: "56", abbreviation: "WY" },
  { name: "District of Columbia", slug: "district-of-columbia", code: "11", abbreviation: "DC" },
];

/**
 * Get state information by slug
 */
export const getStateBySlug = (slug: string): StateMapping | null => {
  return STATE_MAPPINGS.find(state => 
    state.slug.toLowerCase() === slug.toLowerCase()
  ) || null;
};

/**
 * Get state information by name
 */
export const getStateByName = (name: string): StateMapping | null => {
  return STATE_MAPPINGS.find(state => 
    state.name.toLowerCase() === name.toLowerCase()
  ) || null;
};

/**
 * Get state information by area code
 */
export const getStateByCode = (code: string): StateMapping | null => {
  return STATE_MAPPINGS.find(state => state.code === code) || null;
};

/**
 * Get state information by abbreviation
 */
export const getStateByAbbreviation = (abbr: string): StateMapping | null => {
  return STATE_MAPPINGS.find(state => 
    state.abbreviation.toLowerCase() === abbr.toLowerCase()
  ) || null;
};

/**
 * Check if a location parameter is a valid state
 */
export const isValidState = (location: string): boolean => {
  return getStateBySlug(location) !== null;
};

/**
 * Get proper area code for API calls based on location parameter
 * Handles states, MSAs, and national
 */
export const getAreaCodeForLocation = (location: string): string | null => {
  // Check for national/united-states
  if (location === 'united-states' || location === 'national') {
    return '99';
  }
  
  // Check if it's a state
  const state = getStateBySlug(location);
  if (state) {
    return state.code;
  }
  
  // If not a state, it might be an MSA slug
  // MSAs will be resolved via database query in the component
  // For now, return null to indicate it needs database lookup
  return null;
};

/**
 * Format location name for display
 */
export const formatLocationForDisplay = (location: string): string => {
  const state = getStateBySlug(location);
  return state?.name || location;
};